
import { useState } from 'react';

const brewingGuides = [
  {
    id: 'sencha',
    name: '煎茶（緑茶）',
    nameEn: 'Sencha',
    color: '#7B8C3E',
    image: 'https://public.readdy.ai/ai/img_res/60bf1b5ffe8ec859320ec0c3f7c366e2.jpg',
    steps: [
      { icon: 'ri-temp-hot-line', label: '湯温', value: '70〜80℃', note: '沸騰後、少し冷ましてから' },
      { icon: 'ri-scales-3-line', label: '茶葉量', value: 'ティーバッグ1個', note: '約150〜200mlに対して' },
      { icon: 'ri-timer-line', label: '抽出時間', value: '60〜90秒', note: 'お好みで調整してください' },
    ],
    tips: '湯温が高すぎると渋みが出やすくなります。少し冷ましたお湯でじっくり抽出すると、旨味と甘みが引き立ちます。',
  },
  {
    id: 'hojicha',
    name: 'ほうじ茶',
    nameEn: 'Hojicha',
    color: '#A0724A',
    image: 'https://readdy.ai/api/search-image?query=overhead%20view%20of%20Japanese%20roasted%20hojicha%20tea%20being%20brewed%20in%20a%20clear%20glass%20teapot%20warm%20amber%20brown%20color%20with%20roasted%20tea%20leaves%20on%20a%20minimal%20light%20wood%20surface%20warm%20natural%20lighting%20professional%20food%20photography%20clean%20aesthetic&width=600&height=400&seq=brewguide002&orientation=landscape',
    steps: [
      { icon: 'ri-temp-hot-line', label: '湯温', value: '95〜100℃', note: '沸騰したてのお湯でOK' },
      { icon: 'ri-scales-3-line', label: '茶葉量', value: 'ティーバッグ1個', note: '約150〜200mlに対して' },
      { icon: 'ri-timer-line', label: '抽出時間', value: '30〜60秒', note: '香ばしさを楽しむなら短めに' },
    ],
    tips: '高温で素早く淹れるのがポイント。香ばしい焙煎香が一気に広がります。カフェイン控えめなので、夜のリラックスタイムにもおすすめです。',
  },
  {
    id: 'wakocha',
    name: '和紅茶',
    nameEn: 'Japanese Black Tea',
    color: '#C4787A',
    image: 'https://readdy.ai/api/search-image?query=overhead%20view%20of%20Japanese%20black%20tea%20wakocha%20being%20brewed%20in%20a%20clear%20glass%20teapot%20deep%20reddish%20amber%20color%20with%20tea%20leaves%20on%20a%20minimal%20light%20wood%20surface%20warm%20natural%20lighting%20professional%20food%20photography%20clean%20aesthetic&width=600&height=400&seq=brewguide003&orientation=landscape',
    steps: [
      { icon: 'ri-temp-hot-line', label: '湯温', value: '90〜95℃', note: '沸騰後、少しだけ冷まして' },
      { icon: 'ri-scales-3-line', label: '茶葉量', value: 'ティーバッグ1個', note: '約150〜200mlに対して' },
      { icon: 'ri-timer-line', label: '抽出時間', value: '2〜3分', note: 'しっかり抽出がおすすめ' },
    ],
    tips: '海外の紅茶と比べて渋みが少なく、優しい甘みが特徴。ストレートで飲むのがおすすめですが、ミルクとの相性も抜群です。',
  },
  {
    id: 'oolong',
    name: '烏龍茶',
    nameEn: 'Oolong Tea',
    color: '#C4A24A',
    image: 'https://readdy.ai/api/search-image?query=overhead%20view%20of%20Japanese%20oolong%20tea%20being%20brewed%20in%20a%20clear%20glass%20teapot%20golden%20amber%20color%20with%20partially%20oxidized%20tea%20leaves%20on%20a%20minimal%20light%20wood%20surface%20warm%20natural%20lighting%20professional%20food%20photography%20clean%20aesthetic&width=600&height=400&seq=brewguide004&orientation=landscape',
    steps: [
      { icon: 'ri-temp-hot-line', label: '湯温', value: '85〜95℃', note: '高めの温度で香りを引き出す' },
      { icon: 'ri-scales-3-line', label: '茶葉量', value: 'ティーバッグ1個', note: '約150〜200mlに対して' },
      { icon: 'ri-timer-line', label: '抽出時間', value: '90秒〜2分', note: '2煎目も楽しめます' },
    ],
    tips: '1煎目は花のような華やかな香り、2煎目はまろやかな甘みと変化を楽しめます。少し長めに蒸らすと、奥深い味わいに。',
  },
];

export default function TeaBrewingGuide() {
  const [activeTab, setActiveTab] = useState('sencha');
  const activeGuide = brewingGuides.find((g) => g.id === activeTab) || brewingGuides[0];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#FAF8F5]" aria-labelledby="tea-brewing-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-3 py-1.5 bg-[#7B8C3E]/10 text-[#7B8C3E] text-xs tracking-[0.15em] rounded-full mb-4 sm:mb-6">
            BREWING GUIDE
          </span>
          <h2
            id="tea-brewing-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-extralight text-black mb-3 sm:mb-4 tracking-[0.1em]"
          >
            美味しいお茶の淹れ方
          </h2>
          <p className="text-sm sm:text-base text-black/60 font-extralight tracking-wide max-w-xl mx-auto">
            茶葉の種類に合わせた最適な淹れ方で、Craft Teaの味わいを最大限に引き出しましょう
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10 sm:mb-14">
          <div className="inline-flex bg-white rounded-full p-1 shadow-sm">
            {brewingGuides.map((guide) => (
              <button
                key={guide.id}
                onClick={() => setActiveTab(guide.id)}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm tracking-wide whitespace-nowrap cursor-pointer transition-all duration-300 ${
                  activeTab === guide.id
                    ? 'text-white font-light shadow-md'
                    : 'text-black/50 font-extralight hover:text-black/80'
                }`}
                style={
                  activeTab === guide.id
                    ? { backgroundColor: guide.color }
                    : undefined
                }
              >
                {guide.name}
              </button>
            ))}
          </div>
        </div>

        {/* Active Guide Content */}
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[3/2] rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
              <img
                src={activeGuide.image}
                alt={`${activeGuide.name}の淹れ方`}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div
              className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 w-24 h-24 sm:w-32 sm:h-32 rounded-xl sm:rounded-2xl flex flex-col items-center justify-center text-white shadow-lg"
              style={{ backgroundColor: activeGuide.color }}
            >
              <span className="text-[10px] sm:text-xs font-extralight tracking-wider opacity-80 mb-0.5">BEST AT</span>
              <span className="text-xl sm:text-2xl font-extralight">{activeGuide.steps[0].value.split('〜')[0]}</span>
            </div>
          </div>

          {/* Steps & Tips */}
          <div>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: activeGuide.color }}
              ></div>
              <span className="text-xs text-black/40 font-light tracking-[0.15em] uppercase">
                {activeGuide.nameEn}
              </span>
            </div>

            <h3 className="text-2xl sm:text-3xl font-extralight text-black mb-8 sm:mb-10 tracking-[0.05em]">
              {activeGuide.name}の淹れ方
            </h3>

            {/* 3 Steps */}
            <div className="space-y-5 sm:space-y-6 mb-8 sm:mb-10">
              {activeGuide.steps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 sm:gap-5 bg-white rounded-xl p-4 sm:p-5 shadow-sm"
                >
                  <div
                    className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ backgroundColor: `${activeGuide.color}15` }}
                  >
                    <i
                      className={`${step.icon} text-lg sm:text-xl`}
                      style={{ color: activeGuide.color }}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 mb-0.5">
                      <span className="text-xs text-black/40 font-light tracking-wide">{step.label}</span>
                      <span
                        className="text-lg sm:text-xl font-light tracking-wide"
                        style={{ color: activeGuide.color }}
                      >
                        {step.value}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-black/50 font-extralight">{step.note}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tips */}
            <div
              className="rounded-xl p-5 sm:p-6 border-l-4"
              style={{
                borderColor: activeGuide.color,
                backgroundColor: `${activeGuide.color}08`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <i
                  className="ri-lightbulb-line text-base"
                  style={{ color: activeGuide.color }}
                  aria-hidden="true"
                ></i>
                <span
                  className="text-xs font-light tracking-[0.1em]"
                  style={{ color: activeGuide.color }}
                >
                  TIPS
                </span>
              </div>
              <p className="text-sm text-black/60 font-extralight leading-relaxed">
                {activeGuide.tips}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Quick Reference */}
        <div className="mt-14 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {brewingGuides.map((guide) => (
            <button
              key={guide.id}
              onClick={() => setActiveTab(guide.id)}
              className={`group relative p-4 sm:p-5 rounded-xl sm:rounded-2xl text-left cursor-pointer transition-all duration-300 ${
                activeTab === guide.id
                  ? 'bg-white shadow-md ring-1'
                  : 'bg-white/60 hover:bg-white hover:shadow-sm'
              }`}
              style={
                activeTab === guide.id
                  ? { ringColor: `${guide.color}30` }
                  : undefined
              }
            >
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg mb-3"
                style={{ backgroundColor: `${guide.color}15` }}
              >
                <i
                  className="ri-cup-line text-base sm:text-lg"
                  style={{ color: guide.color }}
                  aria-hidden="true"
                ></i>
              </div>
              <h4 className="text-sm sm:text-base font-light text-black mb-1 tracking-wide">
                <a href={`#${guide.id}`} className="hover:underline">
                  {guide.name}
                </a>
              </h4>
              <p className="text-xs text-black/40 font-extralight">
                {guide.steps[0].value} ／ {guide.steps[2].value}
              </p>
              <div
                className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full transition-opacity duration-300"
                style={{
                  backgroundColor: guide.color,
                  opacity: activeTab === guide.id ? 1 : 0,
                }}
              ></div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
